import React, { Fragment, useMemo, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { DatabaseZap, X } from "lucide-react";
import { isSyncableAttachment, useBrainBankSync } from "../../context/BrainBankSyncContext";
import { useComposerBrainBankSync } from "../../hooks/useComposerBrainBankSync";
import { useSession } from "../../context/SessionContext";

interface SentFile {
  file_name: string;
  original_file_name?: string;
}

/**
 * Offers the files already in this conversation to the BrainBank picked for
 * it. Earlier files are never copied on their own: the user chooses, from an
 * inline banner they can dismiss, not a dialog that interrupts the chat.
 */
const EarlierFilesBanner: React.FC<{ messages: any[] }> = ({ messages }) => {
  const { sessionId } = useSession();
  const { mode, brainBank } = useComposerBrainBankSync();
  const {
    getAttachmentSync,
    syncsLoaded,
    syncAttachments,
    earlierFilesDismissed,
    dismissEarlierFiles,
  } = useBrainBankSync();

  // Files sent in this conversation that have no copy in this BrainBank yet.
  const candidates = useMemo(() => {
    if (!brainBank || !sessionId) return [];
    const seen = new Set<string>();
    const files: SentFile[] = [];
    messages.forEach((message) => {
      if (message.role !== "user") return;
      (message.content?.meta?.files || []).forEach((file: SentFile) => {
        if (!file?.file_name || seen.has(file.file_name)) return;
        seen.add(file.file_name);
        const sync = getAttachmentSync(sessionId, file.file_name);
        if (sync && sync.knowledgeBaseId === brainBank.id && sync.status !== "removed") return;
        files.push(file);
      });
    });
    return files;
  }, [messages, brainBank, sessionId, getAttachmentSync]);

  const syncable = candidates.filter((file) => isSyncableAttachment(file.file_name));
  const [unchecked, setUnchecked] = useState<Set<string>>(() => new Set());

  if (
    mode !== "sync" ||
    !brainBank ||
    !sessionId ||
    !syncsLoaded(sessionId) ||
    earlierFilesDismissed(sessionId) ||
    syncable.length === 0
  ) {
    return null;
  }

  const chosen = syncable.filter((file) => !unchecked.has(file.file_name));
  const toggle = (fileName: string) =>
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) next.delete(fileName);
      else next.add(fileName);
      return next;
    });

  const noun = (count: number) => `${count} ${count === 1 ? "file" : "files"}`;
  const summary =
    syncable.length === 1
      ? `1 earlier file in this chat isn't in ${brainBank.name}.`
      : `${syncable.length} earlier files in this chat aren't in ${brainBank.name}.`;

  return (
    <div className="mb-10 flex items-center gap-2 rounded-lg border border-border-default bg-surface-overlay px-3 py-2 text-xs text-muted">
      <DatabaseZap size={14} strokeWidth={1.75} className="shrink-0 text-violet-600" />
      <span className="flex-grow">{summary}</span>

      <Popover className="relative">
        <Popover.Button className="rounded-md border border-violet-300 bg-surface-brand px-2.5 py-1 font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500">
          Add files…
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel className="absolute bottom-full right-0 mb-2 z-20 w-80 rounded-lg bg-surface-overlay p-3 text-primary shadow-lg ring-1 ring-border-default">
            {({ close }) => (
              <div className="space-y-1">
                <div className="px-1 pb-2 text-sm font-semibold">Add to {brainBank.name}</div>
                <div className="max-h-56 overflow-y-auto">
                  {candidates.map((file) => {
                    const supported = isSyncableAttachment(file.file_name);
                    return (
                      <label
                        key={file.file_name}
                        className={`flex items-center gap-2 rounded-md px-1 py-1.5 text-sm ${
                          supported ? "cursor-pointer hover:bg-surface-2" : "text-subtle"
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={!supported}
                          checked={supported && !unchecked.has(file.file_name)}
                          onChange={() => toggle(file.file_name)}
                          className="h-4 w-4 accent-violet-600"
                        />
                        <span className="flex-grow truncate">
                          {file.original_file_name || file.file_name}
                        </span>
                        {!supported && <span className="text-[11px]">Not supported</span>}
                      </label>
                    );
                  })}
                </div>
                <p className="border-t border-border-default px-1 pt-2 text-xs text-muted">
                  Everyone with access to {brainBank.name} can read these files.
                </p>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => close()}
                    className="rounded-lg bg-surface-2 px-3 py-1.5 text-xs font-medium text-primary hover:bg-surface-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={chosen.length === 0}
                    onClick={() => {
                      syncAttachments(sessionId, chosen, {
                        id: brainBank.id,
                        name: brainBank.name,
                      });
                      close();
                    }}
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:bg-surface-3 disabled:text-subtle"
                  >
                    Add {noun(chosen.length)}
                  </button>
                </div>
              </div>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>

      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => dismissEarlierFiles(sessionId)}
        className="rounded-md p-1 text-subtle hover:bg-surface-2 hover:text-primary"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default EarlierFilesBanner;
